/**
 * BRAND COLLECTIONS PANEL
 *
 * Brand-scoped `collectionName` normalizer. Shows the distinct values
 * that appear across the brand's user items and lets the brand owner
 * rename or merge them.
 *
 * NOT included: the "Move to tag" action from the admin CollectionsCleanup
 * page. That's a judgment call about whether a collectionName is
 * really a personal label vs a brand release — kept admin-only. Brand
 * owners get rename/merge only here.
 *
 * Renders as a collapsible section inside the Polishes tab of
 * BrandManagePage. Collapsed by default so brand owners land on the
 * polish tag editor (their primary need); expand only when there's a
 * collection naming issue to fix.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { Brand } from '../../types/brand';
import {
  listBrandCollections,
  renameBrandCollection,
  type BrandCollectionRow,
} from '../../services/brandCollections';
import { getBrandCollections } from '../../services/brandService';
import { PenLine, Merge, Sparkles, X, CheckCircle2 } from 'lucide-react';

interface OfficialCollection {
  id: string;
  name: string;
  itemCount: number;
}

interface Props {
  brand: Brand;
}

export default function BrandCollectionsPanel({ brand }: Props) {
  const [rows, setRows] = useState<BrandCollectionRow[]>([]);
  const [officialCollections, setOfficialCollections] = useState<OfficialCollection[]>([]);
  const [loading, setLoading] = useState(false);
  /** Diagnostic counts from the load — used to render an informative
   *  empty state instead of a bare "no collection names in use yet"
   *  that fires whether the brand actually has zero items or the
   *  query missed them. */
  const [diagnostics, setDiagnostics] = useState<{
    userItemsFound: number;
    itemsWithCollectionName: number;
  } | null>(null);

  // Fetch the brand's official collections (from brands/{id}/collections)
  // so they can be listed above the collector-typed variants. This is
  // the primary picture for the brand owner — their sanctioned releases.
  useEffect(() => {
    if (!brand?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const cols = await getBrandCollections(brand.id);
        if (!cancelled) {
          setOfficialCollections(
            cols.map((c) => ({ id: c.id, name: c.name, itemCount: c.itemCount }))
          );
        }
      } catch (err) {
        console.error('getBrandCollections failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [brand?.id]);
  const [renamingName, setRenamingName] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [mergingName, setMergingName] = useState<string | null>(null);
  const [mergeTarget, setMergeTarget] = useState('');
  const [busyName, setBusyName] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ name: string; text: string; kind: 'ok' | 'err' } | null>(null);

  // Load once on mount. Query is scoped by brand-slug + slug-list so
  // the scan is bounded (2-3 queries regardless of catalog size).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const result = await listBrandCollections(brand.name);
        if (!cancelled) {
          setRows(result.rows);
          setDiagnostics({
            userItemsFound: result.userItemsFound,
            itemsWithCollectionName: result.itemsWithCollectionName,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setFeedback({
            name: '',
            text: `Failed to load collections: ${(err as Error).message}`,
            kind: 'err',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [brand.name]);

  const reload = async () => {
    setLoading(true);
    try {
      const result = await listBrandCollections(brand.name);
      setRows(result.rows);
      setDiagnostics({
        userItemsFound: result.userItemsFound,
        itemsWithCollectionName: result.itemsWithCollectionName,
      });
    } finally {
      setLoading(false);
    }
  };

  const beginRename = (row: BrandCollectionRow) => {
    setRenamingName(row.name);
    setRenameDraft(row.name);
    setMergingName(null);
  };

  const runRename = async (row: BrandCollectionRow) => {
    const to = renameDraft.trim();
    if (!to) {
      setFeedback({ name: row.name, text: 'Enter a new name first.', kind: 'err' });
      return;
    }
    if (to === row.name) {
      setRenamingName(null);
      return;
    }
    if (!window.confirm(
      `Rename collection "${row.name}" → "${to}" on ${row.count} item${row.count === 1 ? '' : 's'}?`
    )) return;
    setBusyName(row.name);
    setFeedback(null);
    try {
      const summary = await renameBrandCollection(brand.name, row.name, to);
      setFeedback({
        name: row.name,
        text: `Renamed on ${summary.written} item${summary.written === 1 ? '' : 's'}.`,
        kind: 'ok',
      });
      setRenamingName(null);
      setRenameDraft('');
      await reload();
    } catch (err) {
      setFeedback({ name: row.name, text: `Rename failed: ${(err as Error).message}`, kind: 'err' });
    } finally {
      setBusyName(null);
    }
  };

  const beginMerge = (row: BrandCollectionRow) => {
    setMergingName(row.name);
    setMergeTarget('');
    setRenamingName(null);
  };

  const runMerge = async (row: BrandCollectionRow) => {
    if (!mergeTarget) {
      setFeedback({ name: row.name, text: 'Choose a target collection to merge into.', kind: 'err' });
      return;
    }
    if (mergeTarget === row.name) {
      setFeedback({ name: row.name, text: 'Cannot merge a collection into itself.', kind: 'err' });
      return;
    }
    if (!window.confirm(
      `Merge "${row.name}" into "${mergeTarget}"?\n\n${row.count} item${row.count === 1 ? '' : 's'} will be moved. Not reversible.`
    )) return;
    setBusyName(row.name);
    setFeedback(null);
    try {
      const summary = await renameBrandCollection(brand.name, row.name, mergeTarget);
      setFeedback({
        name: row.name,
        text: `Merged into "${mergeTarget}". ${summary.written} item${summary.written === 1 ? '' : 's'} moved.`,
        kind: 'ok',
      });
      setMergingName(null);
      setMergeTarget('');
      await reload();
    } catch (err) {
      setFeedback({ name: row.name, text: `Merge failed: ${(err as Error).message}`, kind: 'err' });
    } finally {
      setBusyName(null);
    }
  };

  return (
    <div className="glass-card p-6">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-glass-secondary" />
        <h2 className="text-lg font-semibold text-glass font-body">Collection names</h2>
      </div>
      <p className="text-sm text-glass-secondary font-body mb-4">
        Your official collections are the canonical release names. Below,
        anything collectors have typed as their own personal labels — normalize
        them to match your official names by Rename or Merge.
      </p>

      {/* Official collections (from brands/{id}/collections) — brand's
          canonical release list. Managed on the Collections tab, shown
          here for context so the brand owner can see what
          collector-typed variants should be normalized TO. */}
      <div className="mb-6">
        <div className="text-xs font-medium text-glass-muted uppercase tracking-wide mb-2 flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" /> Your official collections
        </div>
        {officialCollections.length === 0 ? (
          <div className="text-sm text-glass-secondary font-body p-3 bg-slate-800/40 border border-slate-600 rounded">
            No official collections defined yet.{' '}
            <Link
              to={`/brands/${brand.slug}/manage?tab=collections`}
              className="text-purple-300 hover:text-purple-200 underline"
            >
              Create some on the Collections tab
            </Link>
            — they'll become the canonical names collectors normalize TO.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {officialCollections.map((c) => (
              <div
                key={c.id}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded text-sm text-emerald-200 font-body"
              >
                <span className="font-medium">{c.name}</span>
                <span className="text-xs text-emerald-300/70">
                  {c.itemCount} item{c.itemCount === 1 ? '' : 's'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="text-xs font-medium text-glass-muted uppercase tracking-wide mb-2">
        Collector variants
      </div>

      <div>
        {loading && rows.length === 0 ? (
          <div className="text-sm text-glass-secondary font-body">Loading collections…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-glass-secondary font-body space-y-1">
            {diagnostics && diagnostics.userItemsFound === 0 ? (
              <>
                <div>No user items were found for this brand.</div>
                <div className="text-xs text-glass-muted">
                  This is unusual if you know collectors have your polishes.
                  Possible causes: item docs' <code>brandSlug</code>/<code>brandLower</code>
                  fields don't match, OR items lack a <code>slug</code> field
                  and were created before slug stamping was universal.
                </div>
              </>
            ) : diagnostics && diagnostics.itemsWithCollectionName === 0 ? (
              <>
                <div>
                  Found {diagnostics.userItemsFound} item{diagnostics.userItemsFound === 1 ? '' : 's'} for this brand, but none have a <code>collectionName</code> set.
                </div>
                <div className="text-xs text-glass-muted">
                  Collectors haven't assigned any of your polishes to a
                  collection. Once they do, you'll be able to normalize
                  the names here.
                </div>
              </>
            ) : (
              <div>No collection names in use yet.</div>
            )}
          </div>
        ) : (
            <div className="space-y-2">
              {rows.map((row) => {
                const isRenaming = renamingName === row.name;
                const isMerging = mergingName === row.name;
                const isBusy = busyName === row.name;
                const rowFeedback = feedback?.name === row.name ? feedback : null;
                return (
                  <div
                    key={row.name}
                    className="bg-slate-800/40 border border-slate-600 rounded p-3 space-y-2"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-glass font-body truncate">{row.name}</div>
                        <div className="text-xs text-glass-muted font-body">
                          {row.count} item{row.count === 1 ? '' : 's'}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          onClick={() => beginRename(row)}
                          disabled={isBusy}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-slate-700 text-glass rounded hover:bg-slate-600 disabled:opacity-50 font-body"
                        >
                          <PenLine className="w-3 h-3" /> Rename
                        </button>
                        <button
                          onClick={() => beginMerge(row)}
                          disabled={isBusy}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-slate-700 text-glass rounded hover:bg-slate-600 disabled:opacity-50 font-body"
                        >
                          <Merge className="w-3 h-3" /> Merge
                        </button>
                      </div>
                    </div>

                    {isRenaming && (
                      <div className="flex gap-2 items-start">
                        <input
                          type="text"
                          value={renameDraft}
                          onChange={(e) => setRenameDraft(e.target.value)}
                          disabled={isBusy}
                          autoFocus
                          className="flex-1 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-sm text-glass"
                          placeholder="New name"
                        />
                        <button
                          onClick={() => runRename(row)}
                          disabled={isBusy || !renameDraft.trim()}
                          className="px-3 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 font-body"
                        >
                          {isBusy ? '…' : 'Confirm'}
                        </button>
                        <button
                          onClick={() => { setRenamingName(null); setRenameDraft(''); }}
                          disabled={isBusy}
                          className="p-1 text-glass-muted hover:text-glass"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}

                    {isMerging && (
                      <div className="flex gap-2 items-start">
                        <select
                          value={mergeTarget}
                          onChange={(e) => setMergeTarget(e.target.value)}
                          disabled={isBusy}
                          className="flex-1 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-sm text-glass"
                        >
                          <option value="">— Merge into… —</option>
                          {rows
                            .filter((r) => r.name !== row.name)
                            .map((r) => (
                              <option key={r.name} value={r.name}>
                                {r.name} ({r.count})
                              </option>
                            ))}
                        </select>
                        <button
                          onClick={() => runMerge(row)}
                          disabled={isBusy || !mergeTarget}
                          className="px-3 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 font-body"
                        >
                          {isBusy ? '…' : 'Confirm'}
                        </button>
                        <button
                          onClick={() => { setMergingName(null); setMergeTarget(''); }}
                          disabled={isBusy}
                          className="p-1 text-glass-muted hover:text-glass"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}

                    {rowFeedback && (
                      <div
                        className={`text-xs font-body px-2 py-1 rounded ${
                          rowFeedback.kind === 'ok'
                            ? 'bg-emerald-500/10 text-emerald-300'
                            : 'bg-red-500/10 text-red-300'
                        }`}
                      >
                        {rowFeedback.text}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
}
